import { ManualBackgroundCheckStatusChangeParams } from '@/backend/features/background-check';
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { Button } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface BackgroundCheckModalConfirmButtonProps {
    accountId: string;
    backgroundCheckStatus: string | undefined;
    backgroundCheckType: string | undefined;
    backgroundCheckProcess: string | undefined;
    onCloseModal: (closed?: () => void) => void;
    missingProfileInformation: string[];
    onReloadAccounts?: () => void;
    requiresValidation: boolean;
    isFormValid: boolean;
}

export const BackgroundCheckModalConfirmButton: React.FC<BackgroundCheckModalConfirmButtonProps> = ({
    accountId,
    backgroundCheckStatus,
    backgroundCheckType,
    backgroundCheckProcess,
    missingProfileInformation,
    onCloseModal,
    onReloadAccounts,
    requiresValidation,
    isFormValid,
}) => {
    const { t } = useTranslation();
    const functionClient = useFunctionClient();
    const { backgroundCheckTest } = useSelector(getSettings);
    const [submitting, setSubmitting] = React.useState(false);

    const handleRequestBackgroundCheck = async () => {
        setSubmitting(true);
        return functionClient
            .executeFunction({
                functionName: 'requestBackgroundCheck',
                params: {
                    accountId,
                    backgroundCheckType,
                },
            })
            .then(() => {
                showAlertSnackbar({
                    message: backgroundCheckTest.useTestData
                        ? t('backgroundCheck.messages.testBackgroundCheckSubmitted')
                        : t('backgroundCheck.messages.requestSubmitted'),
                    autoHideDuration: 2000,
                    type: 'success',
                });
            })
            .catch(handleErrorDialog)
            .finally(() => {
                setSubmitting(false);
                onCloseModal();
                onReloadAccounts?.();
            });
    };

    const handleContactVolunteer = async () => {
        return functionClient
            .executeFunction({
                functionName: 'contactVolunteer',
                params: {
                    accountId,
                    missingProfileInformation,
                },
            })
            .then(() => {
                showAlertSnackbar({
                    message: t('common.notificationSent'),
                    autoHideDuration: 2000,
                    type: 'success',
                });
            })
            .catch(handleErrorDialog)
            .finally(() => {
                onCloseModal();
                onReloadAccounts?.();
            });
    };

    const handleBackgroundCheckStatusChange = async () => {
        if (!backgroundCheckType) return;

        return functionClient
            .executeFunction({
                functionName: 'manualBackgroundCheckStatusChange',
                params: {
                    accountId,
                    nextStatus: backgroundCheckStatus as ManualBackgroundCheckStatusChangeParams['nextStatus'],
                    type: backgroundCheckType as ManualBackgroundCheckStatusChangeParams['type'],
                },
            })
            .then(() => {
                showAlertSnackbar({
                    message: t('backgroundCheck.messages.saved'),
                    autoHideDuration: 2000,
                    type: 'success',
                });
                onCloseModal();
            })
            .catch(handleErrorDialog)
            .finally(() => {
                onCloseModal();
                onReloadAccounts?.();
            });
    };

    if (backgroundCheckProcess === 'manual' && (isFormValid || !requiresValidation)) {
        return (
            <>
                {!isFormValid && <ContactVolunteerButton onClick={handleContactVolunteer} />}
                <Button
                    color='primary'
                    disabled={!backgroundCheckType || !backgroundCheckStatus}
                    onClick={handleBackgroundCheckStatusChange}
                    data-test='background-check-status-change-button'
                >
                    {t('common.save')}
                </Button>
            </>
        );
    } else if (isFormValid || !requiresValidation) {
        return (
            <Button
                color='primary'
                disabled={!backgroundCheckType || submitting}
                onClick={handleRequestBackgroundCheck}
                data-test='request-background-check-button'
            >
                {t('common.submit')}
            </Button>
        );
    } else {
        return <ContactVolunteerButton onClick={handleContactVolunteer} />;
    }
};

interface ContactVolunteerButtonProps {
    onClick: () => Promise<void>;
}

const ContactVolunteerButton: React.FC<ContactVolunteerButtonProps> = ({ onClick }) => {
    const { t } = useTranslation();

    return (
        <Button color='primary' onClick={onClick} data-test='contact-volunteer-button' startIcon={<CampaignOutlinedIcon />}>
            {t('backgroundCheck.labels.contactVolunteer')}
        </Button>
    );
};
